import Select from "react-select";
import NumberInput from "../../../components/NumberInput";
import { RequiredMark } from "../../../components/FormValidation";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import { getBaseUnitLabel } from "../../../utils/helpers/pricing";
import { calculatePurchaseBaseQuantity } from "../../../utils/helpers/purchaseCalculations";
import {
  applyPurchaseItemInput, changePurchaseInputUnit, createPurchaseItemInput,
  getPurchaseItemInput, getPurchaseUnitOptions, togglePurchasePackaging,
} from "./purchaseItemInput";

const quantityLabel = (value) => Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 });

const PurchaseItemCard = ({ detail, index, materials, units, selectStyles, errors, onChange, onRemove }) => {
  const selectedMaterial = materials.find((material) => String(material.value) === String(detail.material_id));
  const input = getPurchaseItemInput(detail, units);
  const unitOptions = getPurchaseUnitOptions(selectedMaterial?.unit || detail.unit, units, input.unit);
  const unitLabel = unitOptions.find((unit) => unit.value === input.unit)?.label || input.unit || "satuan";
  const prefix = `purchase-item-${index}`;
  const updateInput = (next) => onChange((current) => applyPurchaseItemInput(current, next, units));
  const invalidClass = (field) => errors[field] ? " is-invalid" : "";
  const errorMessage = (field) => errors[field] ? <div id={`${prefix}-${field}-error`} className="invalid-feedback d-block">{errors[field]}</div> : null;
  const validation = (field) => ({ "aria-invalid": Boolean(errors[field]), "aria-describedby": errors[field] ? `${prefix}-${field}-error` : undefined });

  return (
    <article className="purchase-item-card">
      <div className="purchase-item-head">
        <div className="d-flex align-items-center gap-2">
          <span className="purchase-item-number">{String(index + 1).padStart(2, "0")}</span>
          <strong>{detail.material_name || `Item ${index + 1}`}</strong>
        </div>
        <button type="button" className="btn btn-icon btn-ghost-danger" aria-label={`Hapus ${detail.material_name || `item ${index + 1}`}`} onClick={onRemove}>
          <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3" />
          </svg>
        </button>
      </div>
      <div className="purchase-item-fields">
        <div className="purchase-item-main-fields">
          <div className="purchase-item-material-field">
            <label className="form-label" htmlFor={`${prefix}-material`}>Material<RequiredMark /></label>
            <Select
              inputId={`${prefix}-material`} name={`details.${index}.material_id`}
              {...validation("item")} className={`form-select p-0${invalidClass("item")}`} classNamePrefix="akas-select"
              options={materials} styles={selectStyles} menuPortalTarget={document.body} isClearable
              value={selectedMaterial || null} placeholder="Pilih material..." noOptionsMessage={() => "Material tidak tersedia"}
              onChange={(selected) => {
                const next = { ...detail, material_id: selected?.value || "", material_name: selected?.label || "", stock: selected?.stock || 0 };
                const nextInput = selected ? createPurchaseItemInput(selected, units) : { packaged: false, unit: "", quantity: "", package_size: 1, price: "" };
                onChange(() => applyPurchaseItemInput(next, nextInput, units));
              }}
            />
            {errorMessage("item")}
            {selectedMaterial ? <div className="text-secondary small mt-2">Stok tersedia: {quantityLabel(selectedMaterial.stock)} {getBaseUnitLabel(units, selectedMaterial.unit)}</div> : null}
          </div>
          <div>
            <label className="form-label" htmlFor={`${prefix}-quantity`}>{input.packaged ? "Jumlah kemasan" : "Jumlah dibeli"}<RequiredMark /></label>
            <div className="purchase-quantity-controls">
              <NumberInput id={`${prefix}-quantity`} name={`details.${index}.quantity`} {...validation("quantity")}
                className={`form-control purchase-control${invalidClass("quantity")}`} min={input.packaged ? 1 : 0.0001} step={input.packaged ? 1 : "any"}
                value={input.quantity} disabled={!detail.material_id} placeholder="0"
                onChange={(event) => updateInput({ ...input, quantity: event.target.value })} />
              {input.packaged ? <span className="purchase-quantity-suffix">kemasan</span> : (
                <Select inputId={`${prefix}-unit`} aria-label={`Satuan pembelian item ${index + 1}`} {...validation("unit")}
                  className={`purchase-unit-select${invalidClass("unit")}`} classNamePrefix="akas-select" options={unitOptions}
                  styles={selectStyles} menuPortalTarget={document.body} isSearchable={false} isDisabled={!detail.material_id}
                  value={unitOptions.find((unit) => unit.value === input.unit) || null} placeholder="Satuan"
                  onChange={(selected) => updateInput(changePurchaseInputUnit(input, selected.value, units))} />
              )}
            </div>
            {errorMessage("quantity")}
            {!input.packaged && errorMessage("unit")}
          </div>
          <div>
            <label className="form-label" htmlFor={`${prefix}-price`}>Harga per {input.packaged ? "kemasan" : unitLabel}<RequiredMark /></label>
            <div className="input-group purchase-control-group">
              <span className="input-group-text">Rp</span>
              <NumberInput id={`${prefix}-price`} name={`details.${index}.price`} {...validation("price")} className={`form-control${invalidClass("price")}`}
                min="0" step="any" value={input.price} disabled={!detail.material_id} placeholder="0"
                onChange={(event) => updateInput({ ...input, price: event.target.value })} />
            </div>
            {errorMessage("price")}
          </div>
        </div>
        <label className="form-check form-switch purchase-package-toggle">
          <input className="form-check-input" type="checkbox" checked={input.packaged} disabled={!detail.material_id}
            aria-controls={`${prefix}-packaging`} onChange={() => updateInput(togglePurchasePackaging(input))} />
          <span className="form-check-label">Beli per kemasan</span>
        </label>
        {input.packaged ? (
          <div className="purchase-package-fields" id={`${prefix}-packaging`}>
            <div>
              <label className="form-label" htmlFor={`${prefix}-package-size`}>Isi setiap kemasan<RequiredMark /></label>
              <div className="purchase-quantity-controls">
                <NumberInput id={`${prefix}-package-size`} {...validation("package_size")} className={`form-control purchase-control${invalidClass("package_size")}`}
                  min="0.0001" step="any" value={input.package_size} placeholder="0"
                  onChange={(event) => updateInput({ ...input, package_size: event.target.value })} />
                <Select inputId={`${prefix}-unit`} aria-label={`Satuan isi kemasan item ${index + 1}`} {...validation("unit")}
                  className={`purchase-unit-select${invalidClass("unit")}`} classNamePrefix="akas-select" options={unitOptions}
                  styles={selectStyles} menuPortalTarget={document.body} isSearchable={false} value={unitOptions.find((unit) => unit.value === input.unit) || null}
                  onChange={(selected) => updateInput(changePurchaseInputUnit(input, selected.value, units))} />
              </div>
              {errorMessage("package_size")}{errorMessage("unit")}
            </div>
            <span className="text-secondary small">Isi adalah takaran dalam satu kemasan. Harga di atas berlaku untuk satu kemasan.</span>
          </div>
        ) : null}
      </div>
      <div className="purchase-calculation-strip purchase-item-totals" aria-live="polite">
        <div>
          <span>Stok bertambah</span>
          <strong>{quantityLabel(calculatePurchaseBaseQuantity(detail, units))} {getBaseUnitLabel(units, detail.unit)}</strong>
          {input.packaged && Number(input.quantity) > 0 && Number(input.package_size) > 0 ? <span>{quantityLabel(input.quantity)} kemasan × {quantityLabel(input.package_size)} {unitLabel}</span> : null}
        </div>
        <div className="purchase-subtotal"><span>Subtotal</span><strong>{formatIDR(detail.sub_total || 0)}</strong></div>
      </div>
    </article>
  );
};

export default PurchaseItemCard;
